import React, { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useFlag } from '@unleash/proxy-client-react';
import { differenceInDays, differenceInHours, fromUnixTime } from 'date-fns';
import {
  GET_PRECANCEL_URL,
  SELF_SERVE_PORTAL,
  MIGRATE_PMPRO,
  UNCANCEL_SUBSCRIPTION,
  CHECKOUT,
} from '../../../../services/ApolloClient';
import { formatTimestampDateString, navigateWithReload } from '../../../../utils';
import {
  EMembershipGroup,
  EMembershipPlatform,
  EMembershipStatus,
  EMigrationStrategy,
  TMembershipData,
} from '../types';
import {
  getDateTitle,
  getLinksForChargebee,
  getLinksForFlex,
  getLinksForMobile,
  getLinksForPmpro,
  getLinksForUHC,
  getMethods,
  getNameByGroup,
  getStatusFormatted,
  getStatusState,
} from './helpers';
import { useApp, useError } from '../../../../contexts';
import { InfoModal } from '../../../../components/InfoModal';
import { useModal } from '../../../../components/Modal';
import './MembershipTable.scss';

const UPGRADE_PAYPAL = {
  title: 'Upgrade Membership',
  info1:
    'In order to upgrade your membership, you will be prompted to enter your card details on the next screen.',
  info2: 'Please note that you will be credited for any unused time on your current plan.',
};

const UNCANCEL_PAYPAL = {
  title: 'Uncancel Membership',
  info1:
    'In order to uncancel your membership, you will be prompted to enter your card details on the following screen.',
  info2:
    'Please be aware that your next payment will only be charged at the end of the current billing period.',
};

interface IMembershipTablePropTypes {
  data: TMembershipData | null;
}

export function MembershipTable({ data }: Readonly<IMembershipTablePropTypes>) {
  const enableChargebeeRetention = useFlag('enableChargebeeRetention');
  const [openPortal] = useLazyQuery(SELF_SERVE_PORTAL, {
    fetchPolicy: 'no-cache',
  });
  const [checkout] = useLazyQuery(CHECKOUT);
  const [getPrecancelURL] = useLazyQuery(GET_PRECANCEL_URL, {
    fetchPolicy: 'no-cache',
  });
  const [uncancel] = useMutation(UNCANCEL_SUBSCRIPTION);
  const [migratePmpro] = useMutation(MIGRATE_PMPRO);
  const [upgradeId, setUpgradeId] = useState<null | string>(null);
  const [modalInfo, setModalInfo] = useState(UPGRADE_PAYPAL);
  const [debounceValue, setDebounceValue] = useState(false);

  const { showError } = useError();
  const { setLoading } = useApp();
  const { isOpen, closeModal, openModal } = useModal();

  const withDebounce = async (func: () => void | Promise<void>) => {
    try {
      if (debounceValue) {
        setLoading(true);
        return;
      }
      setDebounceValue(true);
      await func();
    } finally {
      setDebounceValue(false);
    }
  };

  const manageSubscription = async () => {
    try {
      setLoading(true);
      const portalRes = await openPortal();
      window.location.href = JSON.parse(portalRes.data.selfServePortal.session).access_url;
    } catch {
      showError();
      setLoading(false);
    }
  };

  const manageUhcSubscription = async () => {
    // Redirect to UHC management page link
    window.location.href = ' https://member.uhc.com/uhc-store/subscriptions/';
  };

  const upgradeSubscription = async (priceId: string) => {
    try {
      setLoading(true);
      const checkoutRes = await checkout({
        variables: { priceId },
      });
      window.location.href = JSON.parse(checkoutRes.data?.checkout?.hostedPage).url;
    } catch {
      setLoading(false);
      showError();
    }
  };

  const upgradeFromTrial = async (priceId: string) => {
    if (data?.status === EMembershipStatus.trial) {
      const trialEnd = fromUnixTime(data.timestamp);
      const now = new Date();
      const daysLeft = Math.max(0, differenceInDays(trialEnd, now));
      const hoursLeft = Math.max(1, differenceInHours(trialEnd, now));

      // Show hours when less than a day remains so users don't see "0 days"
      const timeLeft =
        daysLeft > 0
          ? `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`
          : `${hoursLeft} ${hoursLeft === 1 ? 'hour' : 'hours'}`;

      const trialEndLabel = `${formatTimestampDateString(data.timestamp)} (${timeLeft} remaining)`;

      setModalInfo({
        title: 'Upgrade Membership',
        info1: `Your free trial ends on ${trialEndLabel} — upgrading won't affect your trial period.`,
        info2: '',
      });
      setUpgradeId(priceId);
      openModal();
      return;
    }
    await upgradeSubscription(priceId);
  };

  const upgradePmproSubscription = () => {
    navigateWithReload('/app/join/');
  };

  const cancelSubscription = async () => {
    if (
      enableChargebeeRetention &&
      data?.platform === EMembershipPlatform.chargebee &&
      data.status !== EMembershipStatus.trial
    ) {
      try {
        setLoading(true);
        const precancelData = await getPrecancelURL();
        if (!precancelData?.data?.precancel?.url) {
          throw new Error('Precancel URL not available');
        }
        window.location.href = precancelData?.data?.precancel?.url ?? '';
      } catch {
        setLoading(false);
        showError();
      }
    } else {
      navigateWithReload('/app/cancellation/');
    }
  };

  const migratePmproSubscription = async (
    strategy: EMigrationStrategy,
    chargebeePlanId?: string | null,
    withPortal = true,
  ) => {
    try {
      setLoading(true);
      const migrateRes = await migratePmpro({
        variables: {
          chargebeePlanId: chargebeePlanId ?? null,
          migrationStrategy: strategy,
        },
      });
      if (!withPortal) {
        window.location.reload();
      } else {
        window.location.href =
          migrateRes?.data?.migratePmproSubscriptionToChargebee?.migrationResult?.hostedPageUrl;
      }
    } catch {
      setLoading(false);
      showError();
    }
  };

  const uncancelSubscription = async () => {
    try {
      setLoading(true);
      await uncancel();
      window.location.reload();
    } catch {
      setLoading(false);
      showError();
    }
  };

  const upgradePayPalSubscription = async (chargebeePlanId?: string) => {
    if (!isOpen && chargebeePlanId) {
      setModalInfo(UPGRADE_PAYPAL);
      setUpgradeId(chargebeePlanId);
      openModal();
    }
    if (isOpen && upgradeId) {
      closeModal();
      await migratePmproSubscription(EMigrationStrategy.DURING_UPGRADE, upgradeId);
    }
  };

  const uncancelPayPalSubscription = async () => {
    if (!isOpen) {
      setModalInfo(UNCANCEL_PAYPAL);
      openModal();
    } else {
      closeModal();
      await migratePmproSubscription(EMigrationStrategy.DURING_UNCANCEL);
    }
  };

  const startSubscription = () => {
    navigateWithReload('/members/join/');
  };

  const getLinksWithMethod = () => {
    return getMethods(
      manageSubscription,
      manageUhcSubscription,
      upgradeFromTrial,
      upgradePmproSubscription,
      upgradePayPalSubscription,
      uncancelPayPalSubscription,
      cancelSubscription,
      startSubscription,
      uncancelSubscription,
      migratePmproSubscription,
    );
  };

  const getLinksByStatus = () => {
    let links;
    let currentLinks: Array<string> = [];

    if (!data) {
      currentLinks = ['start'];
    } else if (data?.platform === EMembershipPlatform.chargebee) {
      links = getLinksForChargebee(data.billingPeriodUnit, data.billingPeriod, data.group);
    } else if (data?.platform === EMembershipPlatform.pmpro) {
      links = getLinksForPmpro(data.billingPeriodUnit, data.billingPeriod, data.group);
    } else if (data?.platform === EMembershipPlatform.uhc) {
      links = getLinksForUHC();
    } else if (data?.platform === EMembershipPlatform.flex) {
      links = getLinksForFlex();
    } else {
      links = getLinksForMobile();
    }
    currentLinks = data && links?.[data.status] ? links[data.status] : currentLinks;
    return (
      <div className="membership-links">
        {currentLinks.map((key: string) => (
          <div key={key} className="link">
            <span
              className="action-link"
              onClick={() => {
                withDebounce(getLinksWithMethod()[key].func);
              }}
            >
              {getLinksWithMethod()[key].linkName}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const getModalOkHandler = () => {
    if (upgradeId && data?.status === EMembershipStatus.trial) {
      return async () => {
        closeModal();
        await upgradeSubscription(upgradeId);
      };
    }
    if (upgradeId) {
      return upgradePayPalSubscription;
    }
    return uncancelPayPalSubscription;
  };

  const getModalButtonLabel = () => {
    if (data?.status === EMembershipStatus.trial && upgradeId) {
      return 'Continue';
    }
    return 'OK';
  };

  return (
    <div>
      <InfoModal
        isOpen={isOpen}
        onClose={closeModal}
        onOkClick={getModalOkHandler()}
        title={modalInfo.title}
        info1={modalInfo.info1}
        info2={modalInfo.info2}
        buttonLabel={getModalButtonLabel()}
      />
      <table>
        <thead>
          <tr>
            <th>LEVEL</th>
            <th>STATUS</th>
            <th>{getDateTitle(data?.status)}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div className="table-content">
                {getNameByGroup(data?.group, data?.name, data?.status, data?.platform)}
              </div>
              {getLinksByStatus()}
            </td>
            <td>
              {data?.status && (
                <div className="table-content">
                  <div className={`status-block status-block-${getStatusState(data?.status)}`}>
                    {getStatusFormatted(data?.status)}
                  </div>
                </div>
              )}
            </td>
            <td>
              <div className="table-content">
                {data?.group === EMembershipGroup.lifetime ||
                data?.status === EMembershipStatus.past_due
                  ? '-'
                  : formatTimestampDateString(data?.timestamp)}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
